import {
  employmentStanding as standingOn,
  SEPARATED_EMPLOYMENT_STATUSES,
  type EmploymentStanding,
} from '@micarahr/shared-utils';
import { todayInBusiness } from './business-date';
import { formatDate } from './format';

export type { EmploymentPhase, EmploymentStanding } from '@micarahr/shared-utils';

/** Statuses that mean the separation has already been recorded. */
export const SEPARATED_STATUSES: ReadonlySet<string> = SEPARATED_EMPLOYMENT_STATUSES;

/**
 * One rule for where somebody stands, on every screen that shows it — the
 * list's badge, the panel's header, the Employment tab and the HR overview —
 * and the same rule the server counts and filters by (shared-utils
 * `employmentStanding`, apps/api employees/workforce.ts).
 *
 * The person works their last working day, so up to and including it they
 * are "Separating — leaving on 12 Oct 2026" / "Separating — last day today";
 * from the day after they are separated ("Separated on 30 Sep 2026"). Before
 * the start date they read "Starts …"; separated before it, "Did not start".
 */
export function employmentStanding(
  employee: { employmentStatus: string; dateHired?: string | null; separationDate?: string | null },
  today: string = todayInBusiness(),
): EmploymentStanding {
  return standingOn(employee, today);
}

/**
 * A person named on a record they can no longer be chosen for, with why:
 * "Ramon Tan Ramos (leaving on 16 Oct 2026)", or "(separated on …)" from the
 * day after their last working day. The name alone when nobody is leaving.
 *
 * The pickers leave leavers out of new choices; a record that already names
 * one says so rather than going blank.
 */
export function nameWithLeaving(
  name: string,
  lastWorkingDay: string | null | undefined,
  hasLeft: boolean = !!lastWorkingDay && lastWorkingDay.slice(0, 10) < todayInBusiness(),
): string {
  if (!lastWorkingDay) return name;
  return `${name} (${hasLeft ? 'separated' : 'leaving'} on ${formatDate(lastWorkingDay.slice(0, 10))})`;
}
